import type {ButtonAppearance, ButtonProps, LegacyButtonAppearance, PrimaryOnlyButtonVariant, TypeVariantButtonAppearance} from '@Components/button/button.types';
import {
  ButtonType,
  ButtonVariant,
  LegacyButtonType,
  ON_DARK_BUTTON_CLASS_SUFFIX,
  PRIMARY_BUTTON_APPEARANCE,
  PRIMARY_ONLY_BUTTON_VARIANTS,
  SUCCESS_BUTTON_APPEARANCE,
  UPSELL_BUTTON_APPEARANCE,
} from '@Components/button/button.types';

/**
 * The colour class for `<Button>`. A legacy type renders as its own class, and a button with no type, variant or on-dark
 * stays unstyled so call sites that style themselves through `customClasses` keep their look.
 */
export const getButtonAppearanceClass = (appearance: ButtonAppearance): string => {
  if (isUnstyledButtonAppearance(appearance)) {
    return '';
  }
  if (isLegacyButtonAppearance(appearance)) {
    return appearance.type;
  }
  return getTypeVariantClass(appearance);
};

/**
 * Gives a wrapper's default type to callers that set no type of their own. A primary-only variant keeps the caller on primary,
 * the only type it exists for.
 */
export const getButtonPropsWithDefaultType = (props: ButtonProps, defaultType: ButtonType): ButtonProps => {
  if (props.type !== undefined) {
    return props;
  }
  if (hasPrimaryOnlyButtonVariant(props)) {
    return props;
  }
  return {...props, type: defaultType};
};

const hasPrimaryOnlyButtonVariant = (props: ButtonProps): props is ButtonProps & {variant: PrimaryOnlyButtonVariant} => {
  return isPrimaryOnlyButtonVariant(props.variant);
};

export const isPrimaryOnlyButtonVariant = (variant: ButtonVariant | undefined): variant is PrimaryOnlyButtonVariant => {
  return PRIMARY_ONLY_BUTTON_VARIANTS.some((primaryOnlyVariant) => {
    return primaryOnlyVariant === variant;
  });
};

export const getSuccessFeedbackButtonAppearance = (isShowingSuccess: boolean): ButtonAppearance => {
  if (isShowingSuccess) {
    return SUCCESS_BUTTON_APPEARANCE;
  }
  return PRIMARY_BUTTON_APPEARANCE;
};

export const getUpsellOrSecondaryButtonAppearance = (shouldUpsell: boolean): ButtonAppearance => {
  if (shouldUpsell) {
    return UPSELL_BUTTON_APPEARANCE;
  }
  return {type: ButtonType.SECONDARY};
};

const getTypeVariantClass = (appearance: TypeVariantButtonAppearance): string => {
  const type = appearance.type ?? ButtonType.PRIMARY;
  if (appearance.onDark === true) {
    return `${type}${ON_DARK_BUTTON_CLASS_SUFFIX}`;
  }

  const variant = appearance.variant ?? ButtonVariant.DEFAULT;
  if (variant === ButtonVariant.DEFAULT) {
    return type;
  }
  return `${type}-${variant}`;
};

const isUnstyledButtonAppearance = ({type, variant, onDark}: ButtonAppearance): boolean => {
  return type === undefined && variant === undefined && onDark !== true;
};

const isLegacyButtonAppearance = (appearance: ButtonAppearance): appearance is LegacyButtonAppearance => {
  return appearance.type === LegacyButtonType.PREMIUM3;
};
